import type { FunctionalUseQueryOptions } from 'app/types/network';
import * as yup from 'yup';
import { http } from '../lib/axios';
import { useQueryLPC } from 'app/lib/query';
import type { User } from 'app/types/user';
import type { LatLng } from 'react-native-maps';

export const statisticsSchema = yup.object({
  listings: yup.object({
    offers: yup
      .object({ total: yup.number().integer().min(0).required() })
      .optional()
      .default(undefined),
    requests: yup
      .object({
        total: yup.number().integer().min(0).required(),
      })
      .required(),
  }),
  chat: yup
    .object({
      responseRate: yup.number().integer().optional().nullable(),
      averageResponseTime: yup.number().integer().optional().nullable(),
    })
    .optional()
    .nullable(),
  loans: yup.object({
    incoming: yup
      .object({
        total: yup.number().integer().min(0).required(),
      })
      .required(),
    outgoing: yup
      .object({
        total: yup.number().integer().min(0).required(),
      })
      .required(),
    trustPoints: yup.number().integer().min(0).required(),
  }),
  serviceLoans: yup
    .object({
      incoming: yup
        .object({
          total: yup.number().integer().min(0).required(),
        })
        .required(),
      outgoing: yup
        .object({
          total: yup.number().integer().min(0).required(),
        })
        .required(),
    })
    .optional()
    .nullable(),
  donations: yup
    .object({
      incoming: yup
        .object({
          total: yup.number().integer().min(0).required(),
        })
        .required(),
      outgoing: yup
        .object({
          total: yup.number().integer().min(0).required(),
        })
        .required(),
    })
    .optional()
    .nullable(),
  sales: yup
    .object({
      total: yup.number().integer().min(0).required(),
    })
    .optional()
    .nullable(),
  groups: yup.array(
    yup.object({
      id: yup.string().required(),
      name: yup.string().required(),
    }),
  ),
  subscribed: yup.boolean().optional().nullable(),
  canPublish: yup.boolean().optional().nullable(),
  isSuperRequirements: yup.array(
    yup.object({
      name: yup.string().oneOf(['trustPoints', 'responseRate', 'lastActiveDays']).required(),
      requiredValue: yup.number().required(),
    }),
  ),
});

export type Statistics = yup.InferType<typeof statisticsSchema>;

export interface NeighbourhoodStatusReferral {
  current: number;
  required: number;
  avatars?: string[];
}
export interface NeighbourhoodStatus {
  isMatureNeighbourhood: boolean;
  defricheurEligible: boolean;
  referral?: NeighbourhoodStatusReferral;
  writeToMayor: boolean;
  joinSocialMedia: boolean;
}

export const statisticsKeys = {
  all: ['statistics'] as const,
  details: () => [...statisticsKeys.all, 'detail'] as const,
  detail: (id: User['id']) => [...statisticsKeys.details(), id] as const,
  neighbourhoodStatus: () => [...statisticsKeys.all, 'neighbourhood-status'] as const,
  neighboursCount: (distance?: number, centeredOn?: LatLng, listingsCount = false, recentOnly = false) =>
    [
      ...statisticsKeys.all,
      'neighbours',
      distance,
      centeredOn?.latitude,
      centeredOn?.longitude,
      listingsCount,
      recentOnly,
    ] as const,
};

export function useStatistics(id: User['id'], opts: FunctionalUseQueryOptions<Statistics> = {}) {
  return useQueryLPC<Statistics>({
    queryKey: statisticsKeys.detail(id),
    queryFn: () => http.get<Statistics>(`/statistics/${id}`).then((res) => res.data),
    ...opts,
  });
}

export function useNeighboursCountStatistics(
  distance?: number,
  centeredOn?: LatLng,
  listingsCount = false,
  recentOnly = false,
  onboarding = false,
  opts: FunctionalUseQueryOptions<
    number | { neighboursCount: number; neighbourListingsCount: number; usedRecentOnly: boolean }
  > = {},
) {
  return useQueryLPC<number | { neighboursCount: number; neighbourListingsCount: number; usedRecentOnly: boolean }>({
    queryKey: statisticsKeys.neighboursCount(distance, centeredOn, listingsCount, recentOnly),
    queryFn: () =>
      !distance
        ? 0
        : !centeredOn?.latitude && !centeredOn?.longitude && onboarding
          ? 0
          : http
              .get<number | { neighboursCount: number; neighbourListingsCount: number; usedRecentOnly: boolean }>(
                `/statistics/neighbours?distance=${distance.toFixed(0)}${centeredOn?.latitude && centeredOn.longitude ? `&latitude=${centeredOn.latitude.toFixed(8)}&longitude=${centeredOn.longitude.toFixed(8)}` : ''}&listingsCount=${listingsCount ? 'true' : 'false'}&recentOnly=${recentOnly ? 'true' : 'false'}`,
              )
              .then((res) => res.data),

    ...opts,
  });
}

export function useNeighbourhoodStatus(opts: FunctionalUseQueryOptions<NeighbourhoodStatus> = {}) {
  return useQueryLPC<NeighbourhoodStatus>({
    queryKey: statisticsKeys.neighbourhoodStatus(),
    queryFn: () => http.get<NeighbourhoodStatus>('/statistics/neighbourhood-status').then((res) => res.data),
    ...opts,
  });
}
